import { TransactionReceipt } from '@ethersproject/abstract-provider'
import { getChainId, Network } from '@spritz-finance/sdk'
import { BigNumber, Contract, Signer } from 'ethers'
import { LogDescription } from 'ethers/lib/utils'
import { useCallback, useState } from 'react'
import { useWeb3Connection } from '../Web3Provider'
import { ContractFunctionNames, Falsy, Params, TypedContract } from './types'
import { estimateContractFunctionGasLimit, usePromiseTransaction } from './usePromiseTransaction'

/**
 * @internal Intended for internal use - use it on your own risk
 */
export function connectContractToSigner(contract: Contract, signer?: Signer) {
    if (contract.signer) {
        return contract
    }

    if (signer) {
        return contract.connect(signer)
    }

    throw new TypeError('No signer available in contract, options or library')
}

export function useContractFunction<T extends TypedContract, FN extends ContractFunctionNames<T>>(
    contract: T | Falsy,
    functionName: FN
) {
    const { signer, connectedNetwork } = useWeb3Connection()
    const chainId = connectedNetwork ? getChainId(connectedNetwork) : undefined
    const { promiseTransaction, state, resetState } = usePromiseTransaction<T, FN>(
        chainId,
        functionName
    )

    const [events, setEvents] = useState<LogDescription[] | undefined>(undefined)

    const gasLimitBufferPercentage = 10

    const send = useCallback(
        async (...args: Params<T, FN>): Promise<TransactionReceipt | undefined> => {
            if (contract) {
                const numberOfArgs = contract.interface.getFunction(functionName).inputs.length
                const hasOpts = args.length > numberOfArgs
                if (args.length !== numberOfArgs && args.length !== numberOfArgs + 1) {
                    throw new Error(`Invalid number of arguments for function "${functionName}".`)
                }

                const contractWithSigner = connectContractToSigner(contract, signer)
                const opts = hasOpts ? args[args.length - 1] : undefined

                const estimatedGasLimit =
                    (await estimateContractFunctionGasLimit(
                        contractWithSigner,
                        functionName,
                        args,
                        gasLimitBufferPercentage
                    )) ?? null
                console.log('Estimated gas limit: ', estimatedGasLimit?.toString() ?? 'null')
                const overrideGasLimit =
                    typeof opts === 'object' &&
                    Object.prototype.hasOwnProperty.call(opts, 'gasLimit')
                        ? opts.gasLimit
                        : null

                const overrideGasLimitBn = overrideGasLimit
                    ? BigNumber.from(overrideGasLimit)
                    : null

                let gasLimit: string | undefined
                if (overrideGasLimitBn && estimatedGasLimit) {
                    if (connectedNetwork === Network.Arbitrum) {
                        gasLimit = estimatedGasLimit.toString()
                    } else {
                        gasLimit = estimatedGasLimit.gt(overrideGasLimitBn)
                            ? estimatedGasLimit.toString()
                            : overrideGasLimit
                    }
                } else {
                    gasLimit = estimatedGasLimit?.toString() ?? overrideGasLimit
                }

                console.log('Actual gas limit: ', gasLimit)

                const modifiedOpts = {
                    gasLimit,
                    ...opts,
                }
                const modifiedArgs = hasOpts ? args.slice(0, args.length - 1) : args

                const receipt = await promiseTransaction(
                    contractWithSigner[functionName](...modifiedArgs, modifiedOpts)
                )

                if (receipt?.logs) {
                    const events = receipt.logs.reduce((accumulatedLogs, log) => {
                        try {
                            return log.address.toLowerCase() === contract.address.toLowerCase()
                                ? [...accumulatedLogs, contract.interface.parseLog(log)]
                                : accumulatedLogs
                        } catch (_err) {
                            return accumulatedLogs
                        }
                    }, [] as LogDescription[])
                    setEvents(events)
                }
                return receipt
            }
        },
        [contract, functionName, signer, gasLimitBufferPercentage, promiseTransaction]
    )

    return { send, state, events, resetState }
}
